import { timeout } from 'src_common/common/mobx-utils/timeout';

interface RetryConfigType {
    retries?: number;
    delay?: number; // in milliseconds
    shouldRetry?: (response: RetriableResponseType) => boolean;
}

interface RetriableResponseType {
    status: number;
    [key: string]: unknown;
}

// Default retry only server errors (5xx)
const defaultShouldRetry = (response: RetriableResponseType): boolean => response.status >= 500;

/**
 * Wraps an async function with retry logic.
 *
 * @param callbackFn - The async function to execute.
 * @param config - The retry configuration.
 * @returns The result of the wrapped function.
 */
export const fetchWithRetry = async <T extends RetriableResponseType>(
    callbackFn: () => Promise<T>,
    config?: RetryConfigType
): Promise<T> => {
    const retries = config?.retries ?? 3;
    const delay = config?.delay ?? 500;
    const shouldRetry = config?.shouldRetry ?? defaultShouldRetry;

    let lastError: Error | undefined;
    let lastResponse: T | undefined;

    for (let i = 0; i < retries; i++) {
        try {
            const response = await callbackFn();
            lastResponse = response;

            if (shouldRetry(response) === false) {
                return response;
            }
            lastError = new Error(`API returned status ${response.status}.`);
        } catch (error) {
            lastError = error instanceof Error ? error : new Error('An unknown network error occurred');
            lastResponse = undefined;
        }

        if (i < retries - 1) {
            await timeout(delay);
        }
    }

    if (lastResponse !== undefined) {
        return lastResponse;
    }

    throw lastError;
};
